import { useState } from "react";
import { Link } from "react-router-dom";
import { Flame, Facebook, Twitter, Linkedin, Youtube, Instagram } from "lucide-react";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import api from "../lib/api";
import { toast } from "sonner";

const socials = [
  { icon: Facebook, label: "Facebook", href: "https://facebook.com" },
  { icon: Twitter, label: "Twitter", href: "https://twitter.com" },
  { icon: Linkedin, label: "LinkedIn", href: "https://linkedin.com" },
  { icon: Youtube, label: "YouTube", href: "https://youtube.com" },
  { icon: Instagram, label: "Instagram", href: "https://instagram.com" },
];

export default function Footer() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/newsletter/subscribe", { email });
      toast.success("Subscribed! Daily NFPA codes incoming.");
      setEmail("");
    } catch {
      toast.error("Could not subscribe. Try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <footer className="bg-[#0A0A0A] text-white mt-auto">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 grid md:grid-cols-12 gap-10">
        <div className="md:col-span-5">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-9 h-9 bg-primary flex items-center justify-center">
              <Flame className="w-5 h-5 text-white" />
            </div>
            <span className="font-display font-black text-2xl tracking-tighter">FIRE</span>
          </div>
          <p className="text-gray-400 text-sm max-w-sm leading-relaxed">
            The Fire Sprinkler Industry Network. Connecting contractors, engineers, and suppliers
            around NFPA 13, 14 &amp; 20 — with a live map, code knowledge base, and active forum.
          </p>
          <div className="flex gap-3 mt-6">
            {socials.map((s) => (
              <a key={s.label} href={s.href} target="_blank" rel="noreferrer"
                 aria-label={s.label} data-testid={`social-${s.label.toLowerCase()}`}
                 className="w-10 h-10 border border-gray-700 flex items-center justify-center hover:bg-primary hover:border-primary transition-colors">
                <s.icon className="w-4 h-4" />
              </a>
            ))}
          </div>
        </div>

        <div className="md:col-span-3">
          <h4 className="label-tech text-gray-500 mb-4">Explore</h4>
          <ul className="space-y-2 text-sm text-gray-300">
            <li><Link to="/knowledge" className="hover:text-primary">NFPA Knowledge Base</Link></li>
            <li><Link to="/training" className="hover:text-primary">Training &amp; Courses</Link></li>
            <li><Link to="/map" className="hover:text-primary">Contractor Map</Link></li>
            <li><Link to="/forum" className="hover:text-primary">Community Forum</Link></li>
            <li><Link to="/directory" className="hover:text-primary">Member Directory</Link></li>
          </ul>
        </div>

        <div className="md:col-span-4">
          <h4 className="label-tech text-gray-500 mb-4">Daily Code Digest</h4>
          <p className="text-sm text-gray-400 mb-3">Get a random NFPA code snippet in your inbox every day.</p>
          <form onSubmit={subscribe} className="flex gap-2">
            <Input
              type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com"
              className="rounded-none bg-transparent border-gray-700 text-white placeholder:text-gray-600"
              data-testid="footer-newsletter-input"
            />
            <Button type="submit" disabled={busy} className="rounded-none bg-primary hover:bg-[#D62828] font-bold shrink-0" data-testid="footer-newsletter-submit">
              {busy ? "…" : "Subscribe"}
            </Button>
          </form>
        </div>
      </div>
      <div className="border-t border-gray-800 py-5 text-center text-xs text-gray-500">
        © {new Date().getFullYear()} Fire Sprinkler Network · <a href="https://firesprinkler.us" className="hover:text-primary">firesprinkler.us</a>. Educational reference only; always consult the official NFPA standards.
      </div>
    </footer>
  );
}
